import React, { PropsWithChildren } from 'react';

import { Button, Typography } from '@rrexpress/core';

import classJoin from '@/utility/helper/classJoin';

import { ErrorBoundaryActions } from './error-boundary-actions';

export type ErrorBoundaryState = {
  hasError: boolean,
  detailExpanded: boolean,
  message: string,
  stack: string,
  componentStack: string,
  rawError: {
    error: any,
    info: any,
  }
};

export class ErrorBoundary extends React.Component<PropsWithChildren, ErrorBoundaryState> {
  constructor(props: PropsWithChildren) {
    super(props);
    this.state = {
      hasError: false,
      detailExpanded: false,
      message: '',
      stack: '',
      componentStack: '',
      rawError: {
        error: null,
        info: null,
      },
    };
  }

  componentDidCatch(error: Error, info: any) {
    const errorState: ErrorBoundaryState = {
      hasError: true,
      detailExpanded: false,
      message: 'Unknown Error',
      stack: 'No stack is provided.',
      componentStack: 'No component stack is provided.',
      rawError: {
        error: {
          ...error,
          name: error.name,
          message: error.message,
          cause: error.cause,
          stack: error.stack,
        },
        info,
      },
    };

    if (error instanceof Error) {
      errorState.message = error.message;
      errorState.stack = error.stack!;
    }

    if (info?.componentStack) {
      errorState.componentStack = info.componentStack;
    }

    document.body.scrollTo({ top: 0 });

    console.log('ERROR BOUNDARY CATHCHES THESE ERRORS');
    console.error(error);
    console.info(info);

    this.setState(errorState);
  }

  parseComponentStack(str: string) {
    return str.split('\n').map((s) => s.trim());
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col gap-y-4 items-center mt-32 mb-16">
          <Typography level={3} fontWeight="bold">Terjadi Kesalahan!</Typography>
          <ErrorBoundaryActions />
          <div className="w-[720px]">
            <div className="flex justify-between items-center bg-blue-100 border border-blue-200">
              <div className="px-4">Details</div>
              <Button variant="text" onClick={() => this.setState({ detailExpanded: !this.state.detailExpanded })}>
                {this.state.detailExpanded ? 'Hide' : 'Show'}
              </Button>
            </div>
            <div
              className={classJoin(
                'p-4 border-x border-b border-blue-200',
                this.state.detailExpanded
                  ? 'block'
                  : 'hidden',
              )}
            >
              <div className="flex flex-col gap-y-8">
                <div>
                  <Typography level={4} fontWeight="bold">Message:</Typography>
                  <div className="max-h-[256px] p-4 overflow-auto bg-red-100 border border-red-200">
                    {this.state.message}
                  </div>
                </div>
                <div>
                  <Typography level={4} fontWeight="bold">Stack:</Typography>
                  <div className="max-h-[256px] p-4 overflow-auto bg-red-100 border border-red-200">
                    <div className="font-mono">
                      <pre>{this.state.stack}</pre>
                    </div>
                  </div>
                </div>
                <div>
                  <Typography level={4} fontWeight="bold">Component Stack:</Typography>
                  <div className="max-h-[256px] p-4 overflow-auto bg-red-100 border border-red-200">
                    {this.parseComponentStack(this.state.componentStack).map((str) => (
                      <div className="font-mono">{str}</div>
                    ))}
                  </div>
                </div>
                <div>
                  <Typography level={4} fontWeight="bold">Raw Errors:</Typography>
                  <div className="p-4 overflow-auto bg-red-100 border border-red-200">
                    <pre>{JSON.stringify(this.state.rawError, null, 4)}</pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
